import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  createChangeOrder, 
  updateChangeOrder, 
  deleteChangeOrderItem, 
  approveChangeOrder, 
  setCurrentChangeOrder, 
  releaseChangeOrder, 
  createChangeOrderCostGroup, 
  createChangeOrderCostItem, 
  fetchChangeOrder, 
  removeChangeOrderCostCode,
  updateChangeOrderDisplaySettings,
  updateChangeOrderCostItem,
  manualApproveChangeOrder
} from '../../store/slices/changeOrdersSlice';
import { showToast } from '../utils/Toast';
import { fetchCategories } from '../../store/slices/leadsSlice';
import html2pdf from 'html2pdf.js';

import AddChangeOrderScopeDialog from './AddChangeOrderScopeDialog';
import AddChangeOrderCostCodeDialog from './AddChangeOrderCostCodeDialog';
import ChangeOrderCostItemDialog from './ChangeOrderCostItemDialog';
import ChangeOrderDisplaySettings from './ChangeOrderDisplaySettings';
import { generateChangeOrderTemplate } from '../../data/defaultChangeOrderTemplate';

// Utility function to format currency with commas
const formatCurrency = (amount) => {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return '0.00';
  }
  // Ensure we have a clean number, remove any existing commas or dollar signs
  const cleanAmount = typeof amount === 'string' ? 
    parseFloat(amount.replace(/[$,]/g, '')) : 
    Number(amount);
  
  if (isNaN(cleanAmount)) {
    return '0.00';
  }
  
  return cleanAmount.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

// Utility function to format currency for display (with commas)
const formatCurrencyDisplay = (amount) => {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return '$0.00';
  }
  // Ensure we have a clean number, remove any existing commas or dollar signs
  const cleanAmount = typeof amount === 'string' ? 
    parseFloat(amount.replace(/[$,]/g, '')) : 
    Number(amount);
  
  if (isNaN(cleanAmount)) {
    return '$0.00';
  }
  
  return '$' + cleanAmount.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

// Utility function to round to 2 decimal places
const roundTo2 = (num) => {
  if (num === null || num === undefined || isNaN(num)) return 0;
  return Math.round((Number(num) + Number.EPSILON) * 100) / 100;
};

// Helper function to determine if an item has associated cost codes
const hasAssociatedCostCodes = (scopeItem) => {
  return scopeItem.costCodes && scopeItem.costCodes.length > 0;
};

// Helper to calculate builder cost
const calculateBuilderCost = (quantity, unitCost) => {
  return (Number(quantity) || 0) * (Number(unitCost) || 0);
};

// Helper to calculate client price
const calculateClientPrice = (builderCost, markup) => {
  return (Number(builderCost) || 0) * (1 + ((Number(markup) || 0) / 100));
};

// Helper to calculate margin
const calculateMargin = (builderCost, clientPrice) => {
  if (!clientPrice || clientPrice <= 0) return 0;
  return ((clientPrice - builderCost) / clientPrice) * 100;
};

// Helper to calculate markup
const calculateMarkup = (clientPrice, builderCost) => {
  if (!builderCost || builderCost <= 0) return 0;
  return ((clientPrice / builderCost) - 1) * 100;
};

// Helper to get the total values of all cost codes in a scope item
const calculateScopeTotals = (scopeItem) => {
  if (!scopeItem.costCodes || scopeItem.costCodes.length === 0) {
    return {
      builderCost: scopeItem.builderCost || 0,
      clientPrice: scopeItem.clientPrice || 0,
      profit: (scopeItem.clientPrice || 0) - (scopeItem.builderCost || 0)
    };
  }
  
  return scopeItem.costCodes.reduce((totals, costCode) => {
    totals.builderCost += Number(costCode.builderCost) || 0;
    totals.clientPrice += Number(costCode.clientPrice) || 0;
    totals.profit += (Number(costCode.clientPrice) || 0) - (Number(costCode.builderCost) || 0);
    return totals;
  }, { builderCost: 0, clientPrice: 0, profit: 0 });
};

// Keep the printElement utility function outside since it doesn't use component state
const printElement = (element) => {
  const printWindow = window.open('', '_blank');
  printWindow.document.write(`
    <html>
      <head>
        <title>Print Change Order</title>
        <link href="https://cdn.jsdelivr.net/npm/tailwindcss@2.2.19/dist/tailwind.min.css" rel="stylesheet">
        <style>
          @media print {
            body { 
              padding: 20px;
              font-size: 12pt;
            }
            .no-print { 
              display: none; 
            }
            table { 
              width: 100%;
              border-collapse: collapse;
              margin-bottom: 1rem;
            }
            th, td { 
              border: 1px solid #e5e7eb;
              padding: 8px;
              text-align: left;
            }
            th { 
              background-color: #f3f4f6;
              font-weight: bold;
            }
            .page-break {
              page-break-before: always;
            }
            .change-order-header {
              margin-bottom: 2rem;
            }
            .change-order-footer {
              margin-top: 2rem;
              border-top: 1px solid #e5e7eb;
              padding-top: 1rem;
            }
            @page {
              margin: 0.5in;
            }
          }
        </style>
      </head>
      <body>
        ${element.outerHTML}
      </body>
    </html>
  `);
  printWindow.document.close();
  printWindow.focus();
  setTimeout(() => {
    printWindow.print();
    printWindow.close();
  }, 250);
};

const ChangeOrderDialog = ({ isOpen, onClose, project, currentChangeOrder = null }) => {
  const dispatch = useDispatch();
  const { categories } = useSelector(state => state.leads);
  const [activeTab, setActiveTab] = useState('worksheet');
  const [showAddScope, setShowAddScope] = useState(false);
  const [showAddCostCode, setShowAddCostCode] = useState(false);
  const [currentScopeIndex, setCurrentScopeIndex] = useState(null);
  const [expandedItems, setExpandedItems] = useState({});
  const [closeAfterSave, setCloseAfterSave] = useState(false);
  const [draftChangeOrderId, setDraftChangeOrderId] = useState(null);
  
  // Add local state management for inputs (like proposals)
  const [localInputValues, setLocalInputValues] = useState({});

  // Add formData state
  const [formData, setFormData] = useState({
    category: currentChangeOrder?.category?._id || '',
    items: [],
    reason: currentChangeOrder?.reason || '',
    totalClientPrice: 0,
    totalBuilderCost: 0,
    estimatedProfit: 0,
    displaySettings: {
      showScopeDescription: false,
      showCostCodeDescription: false,
      showQuantity: false,
      showUnit: false,
      showPrice: true,
      showCostCodes: false,
      expandGroups: false,
      separateGroups: false,
      ...currentChangeOrder?.displaySettings
    }
  });

  // Move calculation helper functions inside component
  const calculateBuilderCost = (quantity, unitCost) => {
    return (Number(quantity) || 0) * (Number(unitCost) || 0);
  };

  const calculateClientPrice = (builderCost, markup) => {
    return (Number(builderCost) || 0) * (1 + ((Number(markup) || 0) / 100));
  };

  const calculateMargin = (builderCost, clientPrice) => {
    if (!clientPrice || clientPrice <= 0) return 0;
    return ((clientPrice - builderCost) / clientPrice) * 100;
  };

  const calculateMarkup = (clientPrice, builderCost) => {
    if (!builderCost || builderCost <= 0) return 0;
    return ((clientPrice / builderCost) - 1) * 100;
  };

  // Helper function to get local input key
  const getInputKey = (itemIndex, field, costIndex = null) => {
    return costIndex !== null 
      ? `item_${itemIndex}_cost_${costIndex}_${field}` 
      : `item_${itemIndex}_${field}`;
  };

  // Helper function to get value from local state or formData
  const getInputValue = (itemIndex, field, costIndex = null) => {
    const key = getInputKey(itemIndex, field, costIndex);
    
    if (localInputValues[key] !== undefined) {
      return localInputValues[key];
    }
    
    let value;
    if (costIndex !== null) {
      value = formData.items[itemIndex]?.costCodes?.[costIndex]?.[field];
    } else {
      value = formData.items[itemIndex]?.[field];
    }
    
    // Provide default values to prevent undefined/NaN issues
    if (value === undefined || value === null) {
      if (['quantity'].includes(field)) {
        return 1;
      } else if (['unitCost', 'builderCost', 'clientPrice'].includes(field)) {
        return 0;
      } else if (['markup'].includes(field)) {
        return 49.25;
      } else if (['margin'].includes(field)) {
        return 33;
      } else if (['unit'].includes(field)) {
        return 'EA';
      } else if (['item', 'description'].includes(field)) {
        return '';
      } else if (field === 'profit') {
        // Calculate profit from clientPrice - builderCost
        const itemData = costIndex !== null 
          ? formData.items[itemIndex]?.costCodes?.[costIndex]
          : formData.items[itemIndex];
        const clientPrice = Number(itemData?.clientPrice) || 0;
        const builderCost = Number(itemData?.builderCost) || 0;
        return roundTo2(clientPrice - builderCost);
      } else {
        return value || '';
      }
    }
    
    // Handle profit field calculation
    if (field === 'profit') {
      const itemData = costIndex !== null 
        ? formData.items[itemIndex]?.costCodes?.[costIndex]
        : formData.items[itemIndex];
      const clientPrice = Number(itemData?.clientPrice) || 0;
      const builderCost = Number(itemData?.builderCost) || 0;
      return roundTo2(clientPrice - builderCost);
    }
    
    // Ensure numerical fields return numbers, not NaN, and round to 2 decimals
    if (['unitCost', 'builderCost', 'clientPrice', 'markup', 'margin'].includes(field)) {
      const numValue = Number(value);
      return isNaN(numValue) ? 0 : roundTo2(numValue);
    }
    
    // Quantity should not be rounded to 2 decimals (can be whole numbers)
    if (field === 'quantity') {
      const numValue = Number(value);
      return isNaN(numValue) ? 0 : numValue;
    }
    
    return value;
  };

  const calculateScopeTotals = (scopeItem) => {
    if (!scopeItem.costCodes || scopeItem.costCodes.length === 0) {
      return {
        builderCost: scopeItem.builderCost || 0,
        clientPrice: scopeItem.clientPrice || 0,
        profit: (scopeItem.clientPrice || 0) - (scopeItem.builderCost || 0)
      };
    }
    
    return scopeItem.costCodes.reduce((totals, costCode) => {
      totals.builderCost += Number(costCode.builderCost) || 0;
      totals.clientPrice += Number(costCode.clientPrice) || 0;
      totals.profit += (Number(costCode.clientPrice) || 0) - (Number(costCode.builderCost) || 0);
      return totals;
    }, { builderCost: 0, clientPrice: 0, profit: 0 });
  };

  const calculateTotals = (items) => {
    const totals = items.reduce((acc, item) => {
      if (item.type === 'scope' && item.costCodes && item.costCodes.length > 0) {
        const scopeTotals = calculateScopeTotals(item);
        acc.totalBuilderCost += scopeTotals.builderCost;
        acc.totalClientPrice += scopeTotals.clientPrice;
      } else {
        acc.totalBuilderCost += Number(item.builderCost) || 0;
        acc.totalClientPrice += Number(item.clientPrice) || 0;
      }
      return acc;
    }, {
      totalBuilderCost: 0,
      totalClientPrice: 0
    });

    totals.estimatedProfit = totals.totalClientPrice - totals.totalBuilderCost;
    return totals;
  };

  // Handle input field changes - stores locally and updates calculations immediately
  const handleInputChange = (itemIndex, field, value, costIndex = null) => {
    const key = getInputKey(itemIndex, field, costIndex);
    
    // Update local input value state
    setLocalInputValues(prev => ({
      ...prev,
      [key]: value
    }));

    // Ensure value is properly converted to number for numerical fields
    const processedValue = ['quantity', 'unitCost', 'builderCost', 'clientPrice', 'markup', 'margin', 'profit'].includes(field)
      ? (Number(value) || 0)
      : value;

    // Perform immediate calculations
    if (costIndex !== null) {
      // For cost codes within a scope item
      const updatedCostCodes = [...formData.items[itemIndex].costCodes];
      const currentCostCode = {...updatedCostCodes[costIndex]};
      
      // Update the specified field with the typed value
      currentCostCode[field] = processedValue;
      
      // Perform calculations based on the new value
      if (field === 'quantity' || field === 'unitCost') {
        const quantity = field === 'quantity' ? processedValue : Number(currentCostCode.quantity) || 0;
        const unitCost = field === 'unitCost' ? processedValue : Number(currentCostCode.unitCost) || 0;
        
        // Calculate builder cost
        currentCostCode.builderCost = roundTo2(quantity * unitCost);
        
        // Calculate client price based on markup (allow zero markup)
        const markup = Number(currentCostCode.markup) || 0;
        currentCostCode.clientPrice = roundTo2(currentCostCode.builderCost * (1 + (markup / 100)));
        if (currentCostCode.clientPrice > 0) {
          currentCostCode.margin = roundTo2(((currentCostCode.clientPrice - currentCostCode.builderCost) / currentCostCode.clientPrice) * 100);
        } else {
          currentCostCode.margin = 0;
        }
      } else if (field === 'markup') {
        // Recalculate client price if markup changed (allow zero markup)
        const markup = processedValue;
        currentCostCode.markup = markup;
        currentCostCode.clientPrice = roundTo2(currentCostCode.builderCost * (1 + (markup / 100)));
        if (currentCostCode.clientPrice > 0) {
          currentCostCode.margin = roundTo2(((currentCostCode.clientPrice - currentCostCode.builderCost) / currentCostCode.clientPrice) * 100);
        } else {
          currentCostCode.margin = 0;
        }
      } else if (field === 'clientPrice') {
        // Recalculate margin and markup if client price changed
        const clientPrice = processedValue;
        currentCostCode.clientPrice = clientPrice;
        
        if (currentCostCode.builderCost > 0 && clientPrice > 0) {
          currentCostCode.margin = roundTo2(((clientPrice - currentCostCode.builderCost) / clientPrice) * 100);
          currentCostCode.markup = roundTo2(((clientPrice / currentCostCode.builderCost) - 1) * 100);
        } else {
          currentCostCode.margin = 0;
          currentCostCode.markup = 0;
        }
      } else if (field === 'margin') {
        // Recalculate client price and markup if margin changed (allow zero margin)
        const margin = processedValue;
        const builderCost = Number(currentCostCode.builderCost) || 0;
        
        currentCostCode.margin = margin;
        
        if (margin === 0) {
          // Zero margin means client price equals builder cost
          currentCostCode.clientPrice = builderCost;
          currentCostCode.markup = 0;
        } else if (builderCost > 0 && margin > 0 && margin < 100) {
          // From margin formula: margin = (clientPrice - builderCost) / clientPrice * 100
          // Solve for clientPrice: clientPrice = builderCost / (1 - margin/100)
          const newClientPrice = roundTo2(builderCost / (1 - margin / 100));
          if (isFinite(newClientPrice) && newClientPrice > 0) {
            currentCostCode.clientPrice = newClientPrice;
            
            // Calculate markup from the new client price
            currentCostCode.markup = roundTo2(((newClientPrice / builderCost) - 1) * 100);
          }
        }
      } else if (field === 'builderCost') {
        // Recalculate client price and margin if builder cost changed (allow zero markup)
        const builderCost = processedValue;
        const markup = Number(currentCostCode.markup) ?? 0;
        
        currentCostCode.builderCost = builderCost;
        
        // Calculate client price based on markup (allow zero markup)
        currentCostCode.clientPrice = roundTo2(builderCost * (1 + (markup / 100)));
        
        // Calculate margin
        if (currentCostCode.clientPrice > 0) {
          currentCostCode.margin = roundTo2(((currentCostCode.clientPrice - builderCost) / currentCostCode.clientPrice) * 100);
        } else {
          currentCostCode.margin = 0;
        }
      } else if (field === 'profit') {
        // Recalculate client price and margin if profit changed
        const profit = processedValue;
        const builderCost = Number(currentCostCode.builderCost) || 0;
        
        if (builderCost >= 0 && profit >= 0) {
          // clientPrice = builderCost + profit
          const newClientPrice = roundTo2(builderCost + profit);
          currentCostCode.clientPrice = newClientPrice;
          
          // Calculate margin and markup
          if (newClientPrice > 0) {
            currentCostCode.margin = roundTo2((profit / newClientPrice) * 100);
            if (builderCost > 0) {
              currentCostCode.markup = roundTo2(((newClientPrice / builderCost) - 1) * 100);
            }
          } else {
            currentCostCode.margin = 0;
            currentCostCode.markup = 0;
          }
        }
      }
      
      // Update the cost code in the array
      updatedCostCodes[costIndex] = currentCostCode;
      
      // Call the parent handler to update the form data with all changes at once
      handleItemChange(itemIndex, 'costCodes', updatedCostCodes);
      
      // Update the scope item's totals
      const totals = calculateScopeTotals({ costCodes: updatedCostCodes });
      handleItemChange(itemIndex, 'builderCost', roundTo2(totals.builderCost));
      handleItemChange(itemIndex, 'clientPrice', roundTo2(totals.clientPrice));
      handleItemChange(itemIndex, 'margin', totals.clientPrice > 0 ? roundTo2(((totals.clientPrice - totals.builderCost) / totals.clientPrice) * 100) : 0);
      
    } else {
      // For main item fields (not cost codes)
      // First update the basic field with the typed value
      handleItemChange(itemIndex, field, processedValue);
      
      const item = {...formData.items[itemIndex]};
      item[field] = processedValue;
      
      if (field === 'quantity' || field === 'unitCost') {
        const quantity = field === 'quantity' ? processedValue : Number(item.quantity) || 0;
        const unitCost = field === 'unitCost' ? processedValue : Number(item.unitCost) || 0;
        
        // Calculate builder cost
        const builderCost = roundTo2(quantity * unitCost);
        handleItemChange(itemIndex, 'builderCost', builderCost);
        
        // Calculate client price based on markup (allow zero markup)
        const markup = Number(item.markup) || 0;
        const clientPrice = roundTo2(builderCost * (1 + (markup / 100)));
        handleItemChange(itemIndex, 'clientPrice', clientPrice);
        if (clientPrice > 0) {
          handleItemChange(itemIndex, 'margin', roundTo2(((clientPrice - builderCost) / clientPrice) * 100));
        } else {
          handleItemChange(itemIndex, 'margin', 0);
        }
      } else if (field === 'markup') {
        // Allow zero markup
        const markup = processedValue;
        const builderCost = Number(item.builderCost) || 0;
        
        // Calculate client price (allow zero markup)
        const clientPrice = roundTo2(builderCost * (1 + (markup / 100)));
        handleItemChange(itemIndex, 'clientPrice', clientPrice);
        
        if (clientPrice > 0) {
          handleItemChange(itemIndex, 'margin', roundTo2(((clientPrice - builderCost) / clientPrice) * 100));
        } else {
          handleItemChange(itemIndex, 'margin', 0);
        }
      } else if (field === 'clientPrice') {
        const clientPrice = processedValue;
        const builderCost = Number(item.builderCost) || 0;
        
        if (builderCost > 0 && clientPrice > 0) {
          const margin = roundTo2(((clientPrice - builderCost) / clientPrice) * 100);
          const markup = roundTo2(((clientPrice / builderCost) - 1) * 100);
          handleItemChange(itemIndex, 'margin', margin);
          handleItemChange(itemIndex, 'markup', markup);
        } else {
          handleItemChange(itemIndex, 'margin', 0);
          handleItemChange(itemIndex, 'markup', 0);
        }
      } else if (field === 'builderCost') {
        const builderCost = processedValue;
        const markup = Number(item.markup) ?? 0;
        
        // Calculate client price (allow zero markup)
        const clientPrice = roundTo2(builderCost * (1 + (markup / 100)));
        handleItemChange(itemIndex, 'clientPrice', clientPrice);
        
        if (clientPrice > 0) {
          handleItemChange(itemIndex, 'margin', roundTo2(((clientPrice - builderCost) / clientPrice) * 100));
        } else {
          handleItemChange(itemIndex, 'margin', 0);
        }
      } else if (field === 'margin') {
        // Recalculate client price and markup if margin changed (allow zero margin)
        const margin = processedValue;
        const builderCost = Number(item.builderCost) || 0;
        
        if (margin === 0) {
          // Zero margin means client price equals builder cost
          handleItemChange(itemIndex, 'clientPrice', builderCost);
          handleItemChange(itemIndex, 'markup', 0);
        } else if (builderCost > 0 && margin > 0 && margin < 100) {
          // From margin formula: margin = (clientPrice - builderCost) / clientPrice * 100
          // Solve for clientPrice: clientPrice = builderCost / (1 - margin/100)
          const newClientPrice = roundTo2(builderCost / (1 - margin / 100));
          if (isFinite(newClientPrice) && newClientPrice > 0) {
            handleItemChange(itemIndex, 'clientPrice', newClientPrice);
            
            // Calculate markup from the new client price
            const markup = roundTo2(((newClientPrice / builderCost) - 1) * 100);
            handleItemChange(itemIndex, 'markup', markup);
          }
        }
      } else if (field === 'profit') {
        // Recalculate client price and margin if profit changed
        const profit = processedValue;
        const builderCost = Number(item.builderCost) || 0;
        
        if (builderCost >= 0 && profit >= 0) {
          // clientPrice = builderCost + profit
          const newClientPrice = roundTo2(builderCost + profit);
          handleItemChange(itemIndex, 'clientPrice', newClientPrice);
          
          // Calculate margin and markup
          if (newClientPrice > 0) {
            const margin = roundTo2((profit / newClientPrice) * 100);
            handleItemChange(itemIndex, 'margin', margin);
            if (builderCost > 0) {
              const markup = roundTo2(((newClientPrice / builderCost) - 1) * 100);
              handleItemChange(itemIndex, 'markup', markup);
            }
          } else {
            handleItemChange(itemIndex, 'margin', 0);
            handleItemChange(itemIndex, 'markup', 0);
          }
        }
      }
    }
  };

  // Handle blur event - update cost item directly when editing is complete
  const handleInputBlur = async (itemIndex, field, costIndex = null) => {
    const key = getInputKey(itemIndex, field, costIndex);
    
    // Only proceed if we have a value in localInputValues
    if (localInputValues[key] === undefined) {
      return;
    }
    
    const item = formData.items[itemIndex];
    const value = localInputValues[key];
    
    // Case 1: Direct cost item (not part of a scope group)
    if (item.type === 'cost' && item._id) {
      // Create an update object with the changed field
      let updates = { [field]: value };
      
      // For numerical fields, convert to numbers
      if (['quantity', 'unitCost', 'markup', 'clientPrice', 'builderCost', 'margin'].includes(field)) {
        updates[field] = Number(value);
      }
      
      console.log(`Directly updating cost item ${item._id} field "${field}" to:`, value);
      
      try {
        // Dispatch the update action
        await dispatch(updateChangeOrderCostItem({
          itemId: item._id,
          updates
        })).unwrap();
        
        // After the update, refresh the change order to get all calculated values
        if (currentChangeOrder?._id && project?._id) {
          await dispatch(fetchChangeOrder({
            projectId: project._id,
            changeOrderId: currentChangeOrder._id
          }));
        }
      } catch (error) {
        console.error('Failed to update cost item:', error);
      }
    } 
    // Case 2: Cost code within a scope item
    else if (costIndex !== null && item.costCodes && item.costCodes[costIndex] && item.costCodes[costIndex]._id) {
      const costCode = item.costCodes[costIndex];
      
      // Create an update object with the changed field
      let updates = { [field]: value };
      
      // For numerical fields, convert to numbers
      if (['quantity', 'unitCost', 'markup', 'clientPrice', 'builderCost', 'margin'].includes(field)) {
        updates[field] = Number(value);
      }
      
      console.log(`Directly updating cost code ${costCode._id} field "${field}" to:`, value);
      
      try {
        // Dispatch the update action
        await dispatch(updateChangeOrderCostItem({
          itemId: costCode._id,
          updates
        })).unwrap();
        
        // After the update, refresh the change order to get all calculated values
        if (currentChangeOrder?._id && project?._id) {
          await dispatch(fetchChangeOrder({
            projectId: project._id,
            changeOrderId: currentChangeOrder._id
          }));
        }
      } catch (error) {
        console.error('Failed to update cost code:', error);
      }
    }
    // Case 3: Scope item name/description changes will be handled by the save/draft functionality
  };

  // Handle item change - updates formData state
  const handleItemChange = (itemIndex, field, value) => {
    setFormData(prev => {
      const updatedItems = [...prev.items];
      updatedItems[itemIndex] = {
        ...updatedItems[itemIndex],
        [field]: value
      };

      // Calculate new totals
      const totals = calculateTotals(updatedItems);

      return {
        ...prev,
        items: updatedItems,
        totalBuilderCost: totals.totalBuilderCost,
        totalClientPrice: totals.totalClientPrice,
        estimatedProfit: totals.estimatedProfit
      };
    });
  };

  // getInputValue function moved above - using enhanced version with local state management

  const toggleItemExpansion = (index) => {
    setExpandedItems(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  // Helper functions - continuous editing enabled
  const isEditingDisabled = (status) => {
    return false; // Always allow editing for continuous editing experience
  };

  const isReleaseDisabled = (status) => {
    return ['rejected', 'approved', 'customer_approved', 'customer_rejected'].includes(status);
  };
  
  // Function to check if manual approval should be disabled
  const isManualApproveDisabled = (status) => {
    // Disable for change orders that are already approved or rejected
    return ['customer_approved', 'customer_rejected'].includes(status);
  };
  
  // Handle manual approval of change order
  const handleManualApprove = async () => {
    try {
      if (!currentChangeOrder?._id || !project?._id) {
        showToast('Change order or project information is missing', 'error');
        return;
      }
      
      const result = await dispatch(manualApproveChangeOrder({
        projectId: project._id,
        changeOrderId: currentChangeOrder._id
      })).unwrap();
      
      if (result && result.changeOrder) {
        showToast('Change order manually approved successfully', 'success');
      }
    } catch (error) {
      console.error('Failed to manually approve change order:', error);
      showToast('Failed to manually approve: ' + (error.message || 'Unknown error'), 'error');
    }
  };

  // Fetch categories when dialog opens
  useEffect(() => {
    if (isOpen) {
      dispatch(fetchCategories());
    }
  }, [isOpen, dispatch]);

  // Update the useEffect for populating form data
  useEffect(() => {
    if (isOpen && project) {
      if (currentChangeOrder) {
        console.log('Populating form with existing change order:', currentChangeOrder);
        
        // Map scope of work items
        const scopeItems = currentChangeOrder.scopeOfWork?.map(item => ({
          type: 'scope',
          _id: item.costGroup?._id,
          item: item.costGroup?.name || '',
          description: item.costGroup?.description || '',
          quantity: item.quantity || 1,
          unit: item.unit || '',
          unitCost: item.unitCost || item.costGroup?.builderCost || 0,
          builderCost: item.builderCost || item.costGroup?.builderCost || 0,
          markup: item.markup || item.costGroup?.markup || 0,
          clientPrice: item.clientPrice || item.costGroup?.clientPrice || 0,
          margin: item.margin || item.costGroup?.margin || 0
        })) || [];

        // Map cost code items
        const costItems = currentChangeOrder.costCodes?.map(item => ({
          type: 'cost',
          _id: item.costItem?._id,
          item: item.costItem?.item || '',
          description: item.costItem?.description || '',
          quantity: item.quantity || 1,
          unit: item.unit || '',
          unitCost: item.unitCost || item.costItem?.unitCost || 0,
          builderCost: item.builderCost || item.costItem?.builderCost || 0,
          markup: item.markup || item.costItem?.markup || 0,
          clientPrice: item.clientPrice || item.costItem?.clientPrice || 0,
          margin: item.margin || item.costItem?.margin || 0
        })) || [];

        // Calculate totals
        const allItems = [...scopeItems, ...costItems];
        const totals = calculateTotals(allItems);

        // Set form data for existing change order
        setFormData(prevFormData => ({
          title: currentChangeOrder.title || '',
          // Preserve existing category selection if it exists,
          // otherwise use the currentChangeOrder's category
          category: prevFormData.category || currentChangeOrder.category?._id || '',
          emailAddress: currentChangeOrder.emailAddress || project.client?.email || '',
          status: currentChangeOrder.status || 'draft',
          items: allItems,
          totalClientPrice: totals.totalClientPrice || currentChangeOrder.clientPrice || 0,
          totalBuilderCost: totals.totalBuilderCost || currentChangeOrder.builderCost || 0,
          estimatedProfit: totals.estimatedProfit || currentChangeOrder.estimatedProfit || 0,
          displaySettings: {
            showScopeDescription: false,
            showCostCodeDescription: false,
            showQuantity: false,
            showUnit: false,
            showPrice: true,
            showCostCodes: false,
            expandGroups: false,
            separateGroups: false,
            ...currentChangeOrder.displaySettings
          }
        }));

        // Set draft ID if needed
        if (currentChangeOrder.status === 'draft') {
          setDraftChangeOrderId(currentChangeOrder._id);
        }
      } else {
        console.log('Setting up new change order form');
        // Reset form for new change order, but preserve category if already set
        setFormData(prevFormData => ({
          title: `Change Order for ${project.jobNumber || 'Project'}`,
          // Preserve the category if user already selected one
          category: prevFormData.category || '',
          emailAddress: project?.client?.email || '',
          status: 'draft',
          items: [],
          totalClientPrice: 0,
          totalBuilderCost: 0,
          estimatedProfit: 0,
          displaySettings: {
            showScopeDescription: false,
            showCostCodeDescription: false,
            showQuantity: false,
            showUnit: false,
            showPrice: true,
            showCostCodes: false,
            expandGroups: false,
            separateGroups: false
          }
        }));
        setDraftChangeOrderId(null);
      }
    }
  }, [isOpen, project, currentChangeOrder]);

  // Update the useEffect that handles currentChangeOrder changes
  useEffect(() => {
    if (currentChangeOrder) {
      // Create a set to track all cost code IDs that are part of scope items
      const associatedCostCodeIds = new Set();
      
      // Process scope items (scopeOfWork)
      const scopeItems = (currentChangeOrder.scopeOfWork || [])
        .filter(scope => scope && scope.costGroup)
        .map(scope => {
          // Create the base scope item
          const scopeItem = {
            type: 'scope',
            _id: scope.costGroup._id,
            costGroup: scope.costGroup,
            item: scope.costGroup.name || '',
            description: scope.costGroup.description || '',
            quantity: scope.quantity || 1,
            unit: scope.unit || 'EA',
            unitCost: scope.unitCost || scope.costGroup.builderCost || 0,
            builderCost: scope.builderCost || scope.costGroup.builderCost || 0,
            markup: scope.markup || scope.costGroup.markup || 49.25,
            clientPrice: scope.clientPrice || scope.costGroup.clientPrice || 0,
            margin: scope.margin || scope.costGroup.margin || 33
          };

          // Add cost codes - check both places they could exist
          // 1. In the scopeOfWork.costCodes array (new structure)
          // 2. In the scope.costGroup.costItems array (old structure)
          let costCodes = [];
          
          // Check if costCodes array is populated in the scope
          if (scope.costCodes && scope.costCodes.length > 0) {
            costCodes = scope.costCodes.map(costCode => {
              // Track this cost code ID to avoid duplication
              if (costCode._id) {
                associatedCostCodeIds.add(costCode._id);
              }
              
              return {
                _id: costCode._id,
                item: costCode.item || '',
                description: costCode.description || '',
                quantity: costCode.quantity || 1,
                unit: costCode.unit || 'EA',
                unitCost: costCode.unitCost || 0,
                builderCost: costCode.builderCost || 0,
                markup: costCode.markup || 49.25,
                clientPrice: costCode.clientPrice || 0,
                margin: costCode.margin || 33,
                costGroup: scope.costGroup._id
              };
            });
          } 
          // Fallback to costItems if costCodes is not populated
          else if (scope.costGroup.costItems && scope.costGroup.costItems.length > 0) {
            costCodes = scope.costGroup.costItems.map(costItem => {
              // Track this cost item ID to avoid duplication
              if (costItem._id) {
                associatedCostCodeIds.add(costItem._id);
              }
              
              return {
                _id: costItem._id,
                item: costItem.item || '',
                description: costItem.description || '',
                quantity: costItem.quantity || 1,
                unit: costItem.unit || 'EA',
                unitCost: costItem.unitCost || 0,
                builderCost: costItem.builderCost || 0,
                markup: costItem.markup || 49.25,
                clientPrice: costItem.clientPrice || 0,
                margin: costItem.margin || 33,
                costGroup: scope.costGroup._id
              };
            });
          }
          
          if (costCodes.length > 0) {
            scopeItem.costCodes = costCodes;
          } else {
            scopeItem.costCodes = [];
          }
          
          return scopeItem;
        });

      // Process standalone cost code items (not associated with any scope)
      // Filter out any cost codes that are already part of scope items
      const costItems = (currentChangeOrder.costCodes || [])
        .filter(code => {
          // Skip cost codes with costGroup as they're already part of a scope
          if (code.costGroup) {
            return false;
          }
          
          // Skip cost codes whose IDs are in our associated IDs set
          if (code.costItem && code.costItem._id && associatedCostCodeIds.has(code.costItem._id)) {
            return false;
          }
          
          // Skip cost codes whose IDs directly match an associated ID
          if (code._id && associatedCostCodeIds.has(code._id)) {
            return false;
          }
          
          return code.costItem; // Keep this cost code
        })
        .map(code => ({
          type: 'cost',
          _id: code.costItem._id || code._id,
          item: code.costItem.item || code.item || '',
          description: code.costItem.description || code.description || '',
          quantity: code.quantity || code.costItem.quantity || 1,
          unit: code.unit || code.costItem.unit || 'EA',
          unitCost: code.unitCost || code.costItem.unitCost || 0,
          builderCost: code.builderCost || code.costItem.builderCost || 0,
          markup: code.markup || code.costItem.markup || 49.25,
          clientPrice: code.clientPrice || code.costItem.clientPrice || 0,
          margin: code.margin || code.costItem.margin || 33
        }));

      console.log(`Found ${scopeItems.length} scope items and ${costItems.length} standalone cost items`);
      console.log(`Filtered out ${associatedCostCodeIds.size} associated cost code IDs`);

      const items = [...scopeItems, ...costItems];
      const totals = calculateTotals(items);

      // Set up initial expanded state for items
      const expandedItemsState = {};
      items.forEach((_, index) => {
        expandedItemsState[index] = false;
      });

      // Preserve the existing category selected by the user if it exists
      setFormData(prevFormData => ({
        title: currentChangeOrder.title || '',
        // Preserve the existing category if it exists, otherwise fallback to currentChangeOrder's category
        category: prevFormData.category || currentChangeOrder.category?._id || '',
        emailAddress: currentChangeOrder.emailAddress || '',
        items,
        totalBuilderCost: totals.totalBuilderCost,
        totalClientPrice: totals.totalClientPrice,
        estimatedProfit: totals.totalClientPrice - totals.totalBuilderCost,
        displaySettings: {
          showScopeDescription: false,
          showCostCodeDescription: false,
          showQuantity: false,
          showUnit: false,
          showPrice: true,
          showCostCodes: false,
          expandGroups: false,
          separateGroups: false,
          ...currentChangeOrder.displaySettings
        }
      }));

      // Update expanded items state
      setExpandedItems(expandedItemsState);
    }
  }, [currentChangeOrder]);

  // Update ensureChangeOrderExists to use the stored ID
  const ensureChangeOrderExists = async () => {
    // If we already have a change order (either passed in or created), use it
    if (currentChangeOrder || draftChangeOrderId) {
      return currentChangeOrder || { _id: draftChangeOrderId };
    }

    if (!formData.category) {
      showToast('Please select a category first', 'error');
      return null;
    }

    try {
        const initialChangeOrder = {
          title: `Change Order for ${project.jobNumber || 'Project'}`,
          category: formData.category,
          emailAddress: project.client?.email || '',
          status: 'draft',
          displaySettings: {
            showScopeDescription: false,
            showCostCodeDescription: false,
            showQuantity: false,
            showUnit: false,
            showPrice: true,
            showCostCodes: false,
            expandGroups: false,
            separateGroups: false
          },
          clientPrice: formData.totalClientPrice || 0,
          builderCost: formData.totalBuilderCost || 0,
          estimatedProfit: formData.estimatedProfit || 0
        };
        
        const result = await dispatch(createChangeOrder({
          projectId: project._id,
          changeOrderData: initialChangeOrder
        })).unwrap();
        
        // Store the ID for future use
        setDraftChangeOrderId(result.changeOrder._id);
        // Update current change order in Redux
        dispatch(setCurrentChangeOrder(result.changeOrder));

        return result.changeOrder;
    } catch (error) {
      console.error('Failed to create initial change order:', error);
      showToast('Failed to create change order', 'error');
      return null;
    }
  };

  // Update handleCategoryChange to use ensureChangeOrderExists
  const handleCategoryChange = async (categoryId) => {
    if (!categoryId) {
      showToast('Please select a category', 'error');
      return;
    }

    try {
      // First update local state
      setFormData(prev => ({
        ...prev,
        category: categoryId
      }));

      // If we already have a change order, update it
      if (currentChangeOrder) {
        const result = await dispatch(updateChangeOrder({
          projectId: project._id,
          changeOrderId: currentChangeOrder._id,
          updates: { category: categoryId }
        })).unwrap();
        
        // Update current change order in Redux
        dispatch(setCurrentChangeOrder(result.changeOrder));
      } else {
        // Create new change order with category
        const initialChangeOrder = {
          title: `Change Order for ${project.jobNumber || 'Project'}`,
          category: categoryId,
          emailAddress: project.client?.email || '',
          status: 'draft',
          displaySettings: {
            showScopeDescription: false,
            showCostCodeDescription: false,
            showQuantity: false,
            showUnit: false,
            showPrice: true,
            showCostCodes: false,
            expandGroups: false,
            separateGroups: false
          },
          clientPrice: formData.totalClientPrice || 0,
          builderCost: formData.totalBuilderCost || 0,
          estimatedProfit: formData.estimatedProfit || 0
        };
        
        const result = await dispatch(createChangeOrder({
          projectId: project._id,
          changeOrderData: initialChangeOrder
        })).unwrap();
        
        // Store the ID and update current change order
        setDraftChangeOrderId(result.changeOrder._id);
        dispatch(setCurrentChangeOrder(result.changeOrder));
      }
    } catch (error) {
      console.error('Failed to update category:', error);
      showToast(error.message || 'Failed to update category', 'error');
      
      // Revert local state on error
      setFormData(prev => ({
        ...prev,
        category: currentChangeOrder?.category?._id || ''
      }));
    }
  };

  // Update the handleSaveCostGroup function
  const handleSaveCostGroup = async (scopeData) => {
    try {
      if (!formData.category) {
        showToast('Please select a category first', 'error');
        return;
      }

      // Get or create the change order
      const changeOrder = await ensureChangeOrderExists();
      if (!changeOrder) {
        return;
      }

      let result;
      
      // Check if scopeData already contains a costGroup (response from the dialog)
      if (scopeData._id && scopeData.name) {
        // Use the data directly without making another API call
        result = { 
          costGroup: {
            _id: scopeData._id,
            name: scopeData.name,
            description: scopeData.description,
            builderCost: scopeData.builderCost,
            markup: scopeData.markup,
            clientPrice: scopeData.clientPrice,
            margin: scopeData.margin,
            costItems: scopeData.selectedCostCodes || []
          }
        };
        console.log('Using existing cost group data without additional API call:', result);
      } else {
        // Make the API call if we don't have a complete costGroup yet
        result = await dispatch(createChangeOrderCostGroup({
          ...scopeData,
          projectId: project._id,
          changeOrderId: changeOrder._id,
          category: formData.category // Ensure category is included
        })).unwrap();
        console.log('Created new cost group via API call:', result);
      }

      // If the result includes selected cost codes, we need to add them to our items
      if (result && result.costGroup) {
        // Create a new scope item from the result
        const newScopeItem = {
          type: 'scope',
          _id: result.costGroup._id,
          costGroup: result.costGroup,
          item: result.costGroup.name || '',
          description: result.costGroup.description || '',
          quantity: 1,
          unit: 'EA',
          builderCost: result.costGroup.builderCost || 0,
          markup: result.costGroup.markup || 49.25,
          clientPrice: result.costGroup.clientPrice || 0,
          margin: result.costGroup.margin || 33,
          costCodes: []
        };

        // If there are selected cost codes, add them
        if (scopeData.selectedCostCodes && scopeData.selectedCostCodes.length > 0) {
          newScopeItem.costCodes = scopeData.selectedCostCodes.map(code => ({
            _id: code._id,
            item: code.item || '',
            description: code.description || '',
            quantity: code.quantity || 1,
            unit: code.unit || 'EA',
            unitCost: code.unitCost || 0,
            builderCost: code.builderCost || 0,
            markup: code.markup || 49.25,
            clientPrice: code.clientPrice || 0,
            margin: code.margin || 33,
            costGroup: result.costGroup._id
          }));

          // Update the scope's costs based on the associated cost codes
          const totals = calculateScopeTotals({ costCodes: newScopeItem.costCodes });
          newScopeItem.builderCost = totals.builderCost;
          newScopeItem.clientPrice = totals.clientPrice;
          newScopeItem.margin = calculateMargin(totals.builderCost, totals.clientPrice);
        }

        // Update the form data with the new item
        setFormData(prev => {
          const updatedItems = [...prev.items, newScopeItem];
          const totals = calculateTotals(updatedItems);
          
          return {
            ...prev,
            items: updatedItems,
            totalBuilderCost: totals.totalBuilderCost,
            totalClientPrice: totals.totalClientPrice,
            estimatedProfit: totals.estimatedProfit,
            category: prev.category // Preserve the category
          };
        });
      }

      // Fetch updated change order to refresh the data
      await dispatch(fetchChangeOrder({
        projectId: project._id,
        changeOrderId: changeOrder._id
      })).unwrap();

      setShowAddScope(false);
    } catch (error) {
      console.error('Error saving cost group:', error);
      showToast('Failed to save cost group', 'error');
    }
  };

  // Update the handleSaveCostItem function
  const handleSaveCostItem = async (formData) => {
    console.log('💾 handleSaveCostItem called with:', formData);
    console.log('Current scope index:', currentScopeIndex);
    
    if (!currentChangeOrder) {
      showToast('No change order loaded', 'error');
      return;
    }

    // Multiple items case
    if (formData.multipleItems && formData.count > 0) {
      showToast(`Added ${formData.count} cost codes successfully`, 'success');
      
      // Refresh change order data to get the updated items
      await dispatch(fetchChangeOrder({
        projectId: project._id,
        changeOrderId: currentChangeOrder._id || draftChangeOrderId
      }));
      
      // If we added to a scope item, make sure it's expanded
      if (currentScopeIndex !== null) {
        const scopeItemId = formData.items?.[currentScopeIndex]?._id;
        if (scopeItemId) {
          setExpandedItems(prev => addToExpandedItems(prev, scopeItemId));
        }
        // Reset the currentScopeIndex after adding
        setCurrentScopeIndex(null);
      }
      
      return;
    }
    
    // Single item case
    try {
      const costItemData = {
        projectId: project._id,
        changeOrderId: currentChangeOrder._id || draftChangeOrderId,
        item: formData.item,
        description: formData.description,
        quantity: Number(formData.quantity),
        unit: formData.unit,
        unitCost: Number(formData.unitCost),
        builderCost: Number(formData.builderCost),
        markup: Number(formData.markup),
        clientPrice: Number(formData.clientPrice),
        margin: Number(formData.margin),
        markedAs: formData.markedAs || -1,
      };
      
      // Add costGroup if adding to a scope item
      if (currentScopeIndex !== null && formData.items && formData.items[currentScopeIndex]) {
        costItemData.costGroup = formData.items[currentScopeIndex]._id;
        console.log('Adding cost item to scope:', costItemData.costGroup);
      } else if (formData.costGroup) {
        // If the dialog explicitly set a costGroup, use it
        costItemData.costGroup = formData.costGroup;
        console.log('Dialog provided costGroup:', costItemData.costGroup);
      }
      
      console.log('Final cost item data being sent:', costItemData);
      
      await dispatch(createChangeOrderCostItem(costItemData)).unwrap();
      showToast('Cost code added successfully', 'success');
      
      // Refresh change order data to get the updated item
      await dispatch(fetchChangeOrder({
        projectId: project._id,
        changeOrderId: currentChangeOrder._id || draftChangeOrderId
      }));
      
      // If we added to a scope item, make sure it's expanded
      if (currentScopeIndex !== null) {
        const scopeItemId = formData.items[currentScopeIndex]._id;
        if (scopeItemId) {
          setExpandedItems(prev => addToExpandedItems(prev, scopeItemId));
        }
      }
      
      // Always reset the currentScopeIndex after adding
      setCurrentScopeIndex(null);
      
    } catch (error) {
      showToast(`Failed to add cost code: ${error.message || error}`, 'error');
      console.error('Error adding cost code:', error);
    }
  };

  // Update the handlers to ensure change order exists first
  const handleAddScopeOfWork = async () => {
    if (!formData.category) {
      showToast('Please select a category first', 'error');
      return;
    }
    const changeOrder = await ensureChangeOrderExists();
    if (changeOrder) {
      setShowAddScope(true);
    }
  };

  // Save handlers
  const handleSaveAsDraft = async () => {
    try {
      setCloseAfterSave(true);

      const changeOrder = await ensureChangeOrderExists();
      if (!changeOrder) return;

      // Apply local input values to form data items before saving
      const updatedItems = formData.items.map((item, itemIndex) => {
        // Create a copy of the item
        const updatedItem = { ...item };
        
        // Apply any local input values for this item
        for (const field of ['item', 'description', 'quantity', 'unit', 'unitCost', 'builderCost', 'markup', 'clientPrice', 'margin']) {
          const key = getInputKey(itemIndex, field);
          if (localInputValues[key] !== undefined) {
            // For numeric fields, convert to number
            if (['quantity', 'unitCost', 'builderCost', 'clientPrice', 'markup', 'margin'].includes(field)) {
              updatedItem[field] = Number(localInputValues[key]) || 0;
            } else {
              updatedItem[field] = localInputValues[key];
            }
          }
        }
        
        // Also update any cost codes within this item
        if (updatedItem.costCodes && updatedItem.costCodes.length > 0) {
          updatedItem.costCodes = updatedItem.costCodes.map((code, costIndex) => {
            const updatedCode = { ...code };
            
            for (const field of ['item', 'description', 'quantity', 'unit', 'unitCost', 'builderCost', 'markup', 'clientPrice', 'margin']) {
              const key = getInputKey(itemIndex, field, costIndex);
              if (localInputValues[key] !== undefined) {
                // For numeric fields, convert to number
                if (['quantity', 'unitCost', 'builderCost', 'clientPrice', 'markup', 'margin'].includes(field)) {
                  updatedCode[field] = Number(localInputValues[key]) || 0;
                } else {
                  updatedCode[field] = localInputValues[key];
                }
              }
            }
            
            return updatedCode;
          });
        }
        
        return updatedItem;
      });

      // For scope items, we need to explicitly update the cost group name and description
      // Create an array to track cost group updates that need to be sent
      const costGroupUpdates = [];
      
      // Check for scope item name/description changes in localInputValues
      updatedItems.forEach((item, index) => {
        if (item.type === 'scope' && item._id) {
          const nameKey = getInputKey(index, 'item');
          const descKey = getInputKey(index, 'description');
          
          if (localInputValues[nameKey] !== undefined || localInputValues[descKey] !== undefined) {
            // Add to updates list
            costGroupUpdates.push({
              id: item._id,
              name: localInputValues[nameKey] !== undefined ? localInputValues[nameKey] : item.item,
              description: localInputValues[descKey] !== undefined ? localInputValues[descKey] : item.description
            });
          }
        }
      });
      
      const updatedChangeOrder = {
        title: formData.title || `Change Order for ${project.jobNumber || 'Project'}`,
        category: formData.category,
        emailAddress: formData.emailAddress,
        status: 'draft',
        clientPrice: formData.totalClientPrice || 0,
        builderCost: formData.totalBuilderCost || 0,
        estimatedProfit: formData.estimatedProfit || 0,
        
        // Include cost group updates in the payload
        costGroupUpdates: costGroupUpdates,
        
        scopeOfWork: updatedItems
          .filter(item => item.type === 'scope')
          .map(item => ({
            costGroup: item._id,
            quantity: item.quantity || 1,
            unit: item.unit !== undefined && item.unit !== '' ? item.unit : 'EA',
            unitCost: item.unitCost || 0,
            builderCost: item.builderCost || 0,
            markup: item.markup || 0,
            clientPrice: item.clientPrice || 0,
            margin: item.margin || 0,
            // Transform costCodes to send only relevant data - especially making sure to include unit
            costCodes: item.costCodes && item.costCodes.length > 0 
              ? item.costCodes.map(code => {
                  if (typeof code === 'string') {
                    return code;
                  }
                  return {
                    _id: code._id,
                    item: code.item || '',
                    description: code.description || '',
                    quantity: code.quantity || 1,
                    unit: code.unit !== undefined && code.unit !== '' ? code.unit : 'EA',
                    unitCost: code.unitCost || 0,
                    builderCost: code.builderCost || 0,
                    markup: code.markup || 49.25,
                    clientPrice: code.clientPrice || 0,
                    margin: code.margin || 33,
                    costGroup: item._id
                  };
                })
              : []
          })),

        costCodes: updatedItems
          .filter(item => item.type === 'cost')
          .map(item => ({
            costItem: item._id,
            quantity: item.quantity || 1,
            unit: item.unit !== undefined && item.unit !== '' ? item.unit : 'EA',
            unitCost: item.unitCost || 0,
            builderCost: item.builderCost || 0,
            markup: item.markup || 0,
            clientPrice: item.clientPrice || 0,
            margin: item.margin || 0
          })),

        displaySettings: formData.displaySettings
      };

      const result = await dispatch(updateChangeOrder({
        projectId: project._id,
        changeOrderId: changeOrder._id,
        updates: updatedChangeOrder
      })).unwrap();

      // Refresh the data after saving to ensure we have the latest version
      await dispatch(fetchChangeOrder({
        projectId: project._id,
        changeOrderId: changeOrder._id
      }));

      showToast('Change order saved successfully', 'success');
      onClose();
    } catch (error) {
      console.error('Failed to save change order:', error);
      showToast(error.message || 'Failed to save change order', 'error');
    } finally {
      setCloseAfterSave(false);
    }
  };

  // Update handleSaveAndRelease handler
  const handleSaveAndRelease = async () => {
    try {
      if (!formData.category) {
        showToast('Please select a category', 'error');
        return;
      }

      // Validate client name exists
      if (!project || !project.client) {
        showToast('Client information is missing', 'error');
        return;
      }

      const clientName = project.client.name;
      if (!clientName) {
        showToast('Client name is required', 'error');
        return;
      }

      // Ensure we have a valid email address
      if (!formData.emailAddress) {
        // Try to fallback to project client email if available
        if (project.client && project.client.email) {
          setFormData(prev => ({
            ...prev,
            emailAddress: project.client.email
          }));
        } else {
          showToast('Email address is required', 'error');
          return;
        }
      }

      // Apply local input values to form data items before saving (same as in handleSaveAsDraft)
      const updatedItems = formData.items.map((item, itemIndex) => {
        // Create a copy of the item
        const updatedItem = { ...item };
        
        // Apply any local input values for this item
        for (const field of ['item', 'description', 'quantity', 'unit', 'unitCost', 'builderCost', 'markup', 'clientPrice', 'margin']) {
          const key = getInputKey(itemIndex, field);
          if (localInputValues[key] !== undefined) {
            // For numeric fields, convert to number
            if (['quantity', 'unitCost', 'builderCost', 'clientPrice', 'markup', 'margin'].includes(field)) {
              updatedItem[field] = Number(localInputValues[key]) || 0;
            } else {
              updatedItem[field] = localInputValues[key];
            }
          }
        }
        
        // Also update any cost codes within this item
        if (updatedItem.costCodes && updatedItem.costCodes.length > 0) {
          updatedItem.costCodes = updatedItem.costCodes.map((code, costIndex) => {
            const updatedCode = { ...code };
            
            for (const field of ['item', 'description', 'quantity', 'unit', 'unitCost', 'builderCost', 'markup', 'clientPrice', 'margin']) {
              const key = getInputKey(itemIndex, field, costIndex);
              if (localInputValues[key] !== undefined) {
                // For numeric fields, convert to number
                if (['quantity', 'unitCost', 'builderCost', 'clientPrice', 'markup', 'margin'].includes(field)) {
                  updatedCode[field] = Number(localInputValues[key]) || 0;
                } else {
                  updatedCode[field] = localInputValues[key];
                }
              }
            }
            
            return updatedCode;
          });
        }
        
        return updatedItem;
      });

      // Check for scope item name/description changes in localInputValues
      const costGroupUpdates = [];
      
      updatedItems.forEach((item, index) => {
        if (item.type === 'scope' && item._id) {
          const nameKey = getInputKey(index, 'item');
          const descKey = getInputKey(index, 'description');
          
          if (localInputValues[nameKey] !== undefined || localInputValues[descKey] !== undefined) {
            // Add to updates list
            costGroupUpdates.push({
              id: item._id,
              name: localInputValues[nameKey] !== undefined ? localInputValues[nameKey] : item.item,
              description: localInputValues[descKey] !== undefined ? localInputValues[descKey] : item.description
            });
          }
        }
      });

      // First save/update the change order
      const changeOrderData = {
        title: formData.title || `Change Order for ${project.jobNumber || 'Project'}`,
        category: formData.category,
        emailAddress: formData.emailAddress || project.client?.email || '',
        status: 'draft',
        clientPrice: formData.totalClientPrice || 0,
        builderCost: formData.totalBuilderCost || 0,
        estimatedProfit: formData.estimatedProfit || 0,
        
        // Include cost group updates
        costGroupUpdates: costGroupUpdates,
        
        scopeOfWork: updatedItems
          .filter(item => item.type === 'scope')
          .map(item => ({
            costGroup: item._id,
            quantity: item.quantity || 1,
            unit: item.unit !== undefined && item.unit !== '' ? item.unit : 'EA',
            unitCost: item.unitCost || 0,
            builderCost: item.builderCost || 0,
            markup: item.markup || 0,
            clientPrice: item.clientPrice || 0,
            margin: item.margin || 0,
            // Transform costCodes to send only relevant data - especially making sure to include unit
            costCodes: item.costCodes && item.costCodes.length > 0 
              ? item.costCodes.map(code => {
                  if (typeof code === 'string') {
                    return code;
                  }
                  return {
                    _id: code._id,
                    item: code.item || '',
                    description: code.description || '',
                    quantity: code.quantity || 1,
                    unit: code.unit !== undefined && code.unit !== '' ? code.unit : 'EA',
                    unitCost: code.unitCost || 0,
                    builderCost: code.builderCost || 0,
                    markup: code.markup || 49.25,
                    clientPrice: code.clientPrice || 0,
                    margin: code.margin || 33,
                    costGroup: item._id
                  };
                })
              : []
          })),
        costCodes: updatedItems
          .filter(item => item.type === 'cost')
          .map(item => ({
            costItem: item._id,
            quantity: item.quantity || 1,
            unit: item.unit !== undefined && item.unit !== '' ? item.unit : 'EA',
            unitCost: item.unitCost || 0,
            builderCost: item.builderCost || 0,
            markup: item.markup || 0,
            clientPrice: item.clientPrice || 0,
            margin: item.margin || 0
          })),
        displaySettings: formData.displaySettings
      };

      console.log('Saving change order data:', changeOrderData);

      let savedChangeOrder;
      if (currentChangeOrder?._id) {
        // Update existing change order
        const result = await dispatch(updateChangeOrder({
          projectId: project._id,
          changeOrderId: currentChangeOrder._id,
          updates: changeOrderData
        })).unwrap();
        
        console.log('Updated change order result:', result);
        savedChangeOrder = result.changeOrder;
      } else {
        // Create new change order
        const result = await dispatch(createChangeOrder({
          projectId: project._id,
          changeOrderData
        })).unwrap();
        
        console.log('Created change order result:', result);
        savedChangeOrder = result.changeOrder;
      }

      if (!savedChangeOrder || !savedChangeOrder._id) {
        throw new Error('Failed to save change order: Invalid response from server');
      }

      // Verify the change order was saved correctly
      console.log('SavedChangeOrder before release:', savedChangeOrder);

      // Check for total price field (required for release)
      if (savedChangeOrder.clientPrice === undefined || savedChangeOrder.clientPrice === null) {
        throw new Error('Change order is missing total price information');
      }

      // Then release it
      try {
        const releaseResult = await dispatch(releaseChangeOrder({
          projectId: project._id,
          changeOrderId: savedChangeOrder._id
        })).unwrap();
        
        console.log('Release result:', releaseResult);
        
        if (!releaseResult || !releaseResult.changeOrder) {
          throw new Error('Invalid response when releasing change order');
        }
        
        showToast('Change order released successfully', 'success');
        onClose();
      } catch (releaseError) {
        console.error('Failed to release change order:', releaseError);
        // The change order was saved but not released
        showToast('Failed to release: ' + (releaseError.message || 'Unknown error'), 'error');
        // Keep the dialog open so the user can try again
      }
    } catch (error) {
      console.error('Failed to save and release:', error);
      showToast('Failed to save and release: ' + (error.message || 'Unknown error'), 'error');
    }
  };

  // Then the renderWorksheetTab function
  const renderWorksheetTab = () => (
    <div className="space-y-6">
      {/* Show action buttons only when we have items */}
      {formData.items.length > 0 && (
        <div className="flex justify-end items-center mb-4">
          <div className="flex gap-2">
            <button
              onClick={handleAddScopeOfWork}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Add Scope of Work
            </button>
            <button
              onClick={() => setShowAddCostCode(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Add Cost Codes
            </button>
          </div>
        </div>
      )}

      {formData.items.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 border border-dashed border-gray-300 rounded-lg">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No items in worksheet</h3>
          <p className="mt-1 text-sm text-gray-500">
            Start building your change order by adding scope of work or cost items.
          </p>
          <div className="mt-6 flex justify-center gap-3">
            <button
              onClick={handleAddScopeOfWork}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Add Scope of Work
            </button>
            <button
              onClick={() => setShowAddCostCode(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Add Cost Codes
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          {formData.items.map((item, index) => (
            <div 
              key={index} 
              className={`border rounded-lg ${
                item.type === 'scope' ? 'bg-blue-50' : 'bg-green-50'
              }`}
            >
              {/* Header section */}
              <div className="p-4 border-b">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-4">
                      <div className="text-sm font-medium text-gray-500">
                        {item.type === 'scope' ? 'Scope of Work' : 'Cost Code'}
                      </div>
                      <div className="flex items-center">
                        {/* Add Cost Code button for scope items */}
                        {item.type === 'scope' && (
                          <button
                            onClick={() => handleOpenCostCodeDialog(index)}
                            className="mr-3 flex items-center px-3 py-1.5 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-md transition-colors duration-150 border border-blue-200"
                            title="Add cost items to this scope of work"
                          >
                            <svg 
                              className="w-5 h-5 mr-2" 
                              fill="none" 
                              viewBox="0 0 24 24" 
                              stroke="currentColor"
                              aria-hidden="true"
                            >
                              <path 
                                strokeLinecap="round" 
                                strokeLinejoin="round" 
                                strokeWidth="2" 
                                d="M12 6v6m0 0v6m0-6h6m-6 0H6" 
                              />
                            </svg>
                            <span className="font-medium">Add Cost Items to Scope</span>
                            <span className="sr-only">Click to add cost items to this scope of work</span>
                          </button>
                        )}
                        <button
                          onClick={async () => {
                            if (!currentChangeOrder?._id) {
                              showToast('Cannot delete item: Change order not found', 'error');
                              return;
                            }
                            
                            if (!item._id) {
                              // If the item doesn't have an ID, it's only in local state
                              const updatedItems = formData.items.filter((_, i) => i !== index);
                              setFormData(prev => {
                                const totals = calculateTotals(updatedItems);
                                return {
                                  ...prev,
                                  items: updatedItems,
                                  totalBuilderCost: totals.totalBuilderCost,
                                  totalClientPrice: totals.totalClientPrice,
                                  estimatedProfit: totals.totalClientPrice - totals.totalBuilderCost,
                                  category: prev.category // Preserve the category
                                };
                              });
                              return;
                            }

                            try {
                              // Call the API to delete the item
                              await dispatch(deleteChangeOrderItem({
                                changeOrderId: currentChangeOrder._id,
                                itemId: item._id,
                                itemType: item.type === 'scope' ? 'scope' : 'costCode'
                              })).unwrap();
                              
                              // Update local state after successful deletion
                              const updatedItems = formData.items.filter((_, i) => i !== index);
                              setFormData(prev => {
                                const totals = calculateTotals(updatedItems);
                                return {
                                  ...prev,
                                  items: updatedItems,
                                  totalBuilderCost: totals.totalBuilderCost,
                                  totalClientPrice: totals.totalClientPrice,
                                  estimatedProfit: totals.totalClientPrice - totals.totalBuilderCost,
                                  category: prev.category // Preserve the category
                                };
                              });
                            } catch (error) {
                              console.error('Failed to delete item:', error);
                              showToast(error.message || 'Failed to delete item', 'error');
                            }
                          }}
                          className="text-red-600 hover:text-red-800"
                          title="Delete item"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" 
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" 
                            />
                          </svg>
                        </button>
                      </div>
                    </div>

                    {/* Always visible fields */}
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Item Name
                        </label>
                        <input
                          type="text"
                          value={getInputValue(index, 'item')}
                          onChange={(e) => handleInputChange(index, 'item', e.target.value)}
                          onBlur={() => handleInputBlur(index, 'item')}
                          className="w-full border rounded px-2 py-1"
                        />
                      </div>
                      {item.type === 'scope' && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Description
                          </label>
                          <textarea
                            value={getInputValue(index, 'description')}
                            onChange={(e) => handleInputChange(index, 'description', e.target.value)}
                            onBlur={() => handleInputBlur(index, 'description')}
                            rows={6}
                            className="w-full border rounded px-2 py-1 resize-vertical"
                          />
                        </div>
                      )}
                    </div>

                    {/* Quick summary of numerical values */}
                    <div className="mt-4 flex items-center justify-between text-sm">
                      {item.type === 'cost' && (
                        <div className="text-gray-600">
                          Client Price: ${item.clientPrice ? Number(item.clientPrice).toFixed(2) : '0.00'}
                        </div>
                      )}
                      {/* For scope items, show a summary of cost codes if they exist */}
                     
                      {/* Toggle button for expanding/collapsing */}
                      {item.type === 'scope' && (
                        <button
                          onClick={() => toggleItemExpansion(index)}
                          className="text-blue-600 hover:text-blue-800 flex items-center"
                        >
                          {expandedItems[index] ? 'Hide Details' : 'Show Details'}
                          <svg 
                            className={`ml-1 w-4 h-4 transform ${expandedItems[index] ? 'rotate-180' : ''}`}
                            fill="none" 
                            stroke="currentColor" 
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Collapsible numerical fields - always show for cost type */}
              {(expandedItems[index] || item.type === 'cost') && (
                <div className="p-4 border-t bg-white bg-opacity-50">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {item.type === 'cost' && item.description && (
                      <div className="col-span-2 md:col-span-4 mb-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Description
                        </label>
                        <textarea
                          value={getInputValue(index, 'description')}
                          onChange={(e) => handleInputChange(index, 'description', e.target.value)}
                          onBlur={() => handleInputBlur(index, 'description')}
                          rows={3}
                          className="w-full border rounded px-2 py-1 resize-vertical"
                        />
                      </div>
                    )}
                    
                    {/* Show quantity, unit, unit cost and markup only for cost items, not for scope of work */}
                    {item.type === 'cost' && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Quantity
                          </label>
                          <input
                            type="number"
                            value={getInputValue(index, 'quantity')}
                            onChange={(e) => handleInputChange(index, 'quantity', e.target.value)}
                            onBlur={() => handleInputBlur(index, 'quantity')}
                            className="w-full border rounded px-2 py-1"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Unit
                          </label>
                          <input
                            type="text"
                            value={getInputValue(index, 'unit')}
                            onChange={(e) => handleInputChange(index, 'unit', e.target.value)}
                            onBlur={() => handleInputBlur(index, 'unit')}
                            className="w-full border rounded px-2 py-1"
                            placeholder="SQ"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Unit Cost
                          </label>
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                            <input
                              type="number"
                              value={getInputValue(index, 'unitCost')}
                              onChange={(e) => handleInputChange(index, 'unitCost', e.target.value)}
                              onBlur={() => handleInputBlur(index, 'unitCost')}
                              className="w-full border rounded px-2 py-1 pl-7"
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Builder Cost
                          </label>
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                            <input
                              type="number"
                              value={getInputValue(index, 'builderCost')}
                              onChange={(e) => handleInputChange(index, 'builderCost', e.target.value)}
                              onBlur={() => handleInputBlur(index, 'builderCost')}
                              className="w-full border rounded px-2 py-1 pl-7"
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Markup %
                          </label>
                          <input
                            type="number"
                            value={getInputValue(index, 'markup')}
                            onChange={(e) => handleInputChange(index, 'markup', e.target.value)}
                            onBlur={() => handleInputBlur(index, 'markup')}
                            className="w-full border rounded px-2 py-1"
                            placeholder="49.25"
                            step="any"
                          />
                          <small className="text-gray-500">Default: 49.25%</small>
                        </div>
                      
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Client Price
                          </label>
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                            <input
                              type="number"
                              value={getInputValue(index, 'clientPrice')}
                              onChange={(e) => handleInputChange(index, 'clientPrice', e.target.value)}
                              onBlur={() => handleInputBlur(index, 'clientPrice')}
                              className="w-full border rounded px-2 py-1 pl-7"
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Margin %
                          </label>
                          <input
                            type="number"
                            value={getInputValue(index, 'margin')}
                            onChange={(e) => handleInputChange(index, 'margin', e.target.value)}
                            onBlur={() => handleInputBlur(index, 'margin')}
                            className="w-full border rounded px-2 py-1"
                            placeholder="33"
                          />
                          <small className="text-gray-500">Default: 33%</small>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Profit
                          </label>
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                            <input
                              type="number"
                              value={getInputValue(index, 'profit')}
                              onChange={(e) => handleInputChange(index, 'profit', e.target.value)}
                              onBlur={() => handleInputBlur(index, 'profit')}
                              className="w-full border rounded px-2 py-1 pl-7"
                            />
                          </div>
                        </div>
                      </>
                    )}
                    
                    {/* For scope items, show group pricing */}
                    {item.type === 'scope' && !hasAssociatedCostCodes(item) && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Builder Cost
                          </label>
                          <input
                            type="number"
                            value={getInputValue(index, 'builderCost')}
                            onChange={(e) => handleInputChange(index, 'builderCost', e.target.value)}
                            onBlur={() => handleInputBlur(index, 'builderCost')}
                            className="w-full border rounded px-2 py-1"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Markup %
                          </label>
                          <input
                            type="number"
                            value={getInputValue(index, 'markup')}
                            onChange={(e) => handleInputChange(index, 'markup', e.target.value)}
                            onBlur={() => handleInputBlur(index, 'markup')}
                            className="w-full border rounded px-2 py-1"
                            placeholder="49.25"
                            step="any"
                          />
                          <small className="text-gray-500">Default: 49.25%</small>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Client Price
                          </label>
                          <input
                            type="number"
                            value={getInputValue(index, 'clientPrice')}
                            onChange={(e) => handleInputChange(index, 'clientPrice', e.target.value)}
                            onBlur={() => handleInputBlur(index, 'clientPrice')}
                            className="w-full border rounded px-2 py-1"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Profit
                          </label>
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                            <input
                              type="number"
                              value={getInputValue(index, 'profit')}
                              onChange={(e) => handleInputChange(index, 'profit', e.target.value)}
                              onBlur={() => handleInputBlur(index, 'profit')}
                              className="w-full border rounded px-2 py-1 pl-7"
                            />
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Render associated cost codes if this is a scope item with cost codes */}
              {item.type === 'scope' && hasAssociatedCostCodes(item) && (
                <div className="mt-2 p-4 border-t">
                  <div className="flex justify-between items-center mb-3">
                    <h4 className="text-sm font-medium text-gray-700">Associated Cost Codes</h4>
                    <div className="text-sm text-blue-600">
                      Total: ${calculateScopeTotals(item).clientPrice.toFixed(2)}
                    </div>
                  </div>
                  <div className="space-y-4">
                    {item.costCodes.map((costCode, costIndex) => (
                      <div key={costIndex} className="border border-green-200 rounded-lg bg-green-50 p-3">
                        <div className="flex justify-between items-center mb-2">
                          <div className="font-medium">{costCode.item}</div>
                          <button
                            onClick={() => handleRemoveCostCode(index, costIndex, costCode._id)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>

                        {costCode.description && (
                          <div className="mb-3">
                            <label className="block text-xs text-gray-500 mb-1">Description</label>
                            <textarea
                              value={getInputValue(index, 'description', costIndex)}
                              onChange={(e) => handleInputChange(index, 'description', e.target.value, costIndex)}
                              onBlur={() => handleInputBlur(index, 'description', costIndex)}
                              rows={2}
                              className="w-full border rounded px-2 py-1 text-sm resize-vertical"
                            />
                          </div>
                        )}

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Quantity</label>
                            <input
                              type="number"
                              value={getInputValue(index, 'quantity', costIndex)}
                              onChange={(e) => handleInputChange(index, 'quantity', e.target.value, costIndex)}
                              onBlur={() => handleInputBlur(index, 'quantity', costIndex)}
                              className="w-full border rounded px-2 py-1 text-sm"
                            />
                          </div>

                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Unit</label>
                            <input
                              type="text"
                              value={getInputValue(index, 'unit', costIndex)}
                              onChange={(e) => handleInputChange(index, 'unit', e.target.value, costIndex)}
                              onBlur={() => handleInputBlur(index, 'unit', costIndex)}
                              className="w-full border rounded px-2 py-1 text-sm"
                            />
                          </div>

                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Unit Cost</label>
                            <div className="relative">
                              <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">$</span>
                              <input
                                type="number"
                                value={getInputValue(index, 'unitCost', costIndex)}
                                onChange={(e) => handleInputChange(index, 'unitCost', e.target.value, costIndex)}
                                onBlur={() => handleInputBlur(index, 'unitCost', costIndex)}
                                className="w-full border rounded px-2 py-1 pl-7 text-sm"
                              />
                            </div>
                          </div>

                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Builder Cost</label>
                            <div className="relative">
                              <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">$</span>
                              <input
                                type="number"
                                value={getInputValue(index, 'builderCost', costIndex)}
                                onChange={(e) => handleInputChange(index, 'builderCost', e.target.value, costIndex)}
                                onBlur={() => handleInputBlur(index, 'builderCost', costIndex)}
                                className="w-full border rounded px-2 py-1 pl-7 text-sm"
                              />
                            </div>
                          </div>

                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Markup %</label>
                            <input
                              type="number"
                              value={getInputValue(index, 'markup', costIndex)}
                              onChange={(e) => handleInputChange(index, 'markup', e.target.value, costIndex)}
                              onBlur={() => handleInputBlur(index, 'markup', costIndex)}
                              className="w-full border rounded px-2 py-1 text-sm"
                            />
                          </div>

                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Client Price</label>
                            <div className="relative">
                              <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">$</span>
                              <input
                                type="number"
                                value={getInputValue(index, 'clientPrice', costIndex)}
                                onChange={(e) => handleInputChange(index, 'clientPrice', e.target.value, costIndex)}
                                onBlur={() => handleInputBlur(index, 'clientPrice', costIndex)}
                                className="w-full border rounded px-2 py-1 pl-7 text-sm"
                              />
                            </div>
                          </div>

                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Margin %</label>
                            <input
                              type="number"
                              value={getInputValue(index, 'margin', costIndex)}
                              onChange={(e) => handleInputChange(index, 'margin', e.target.value, costIndex)}
                              onBlur={() => handleInputBlur(index, 'margin', costIndex)}
                              className="w-full border rounded px-2 py-1 text-sm"
                              placeholder="33"
                            />
                          </div>

                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Profit</label>
                            <div className="relative">
                              <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">$</span>
                              <input
                                type="number"
                                value={getInputValue(index, 'profit', costIndex)}
                                onChange={(e) => handleInputChange(index, 'profit', e.target.value, costIndex)}
                                onBlur={() => handleInputBlur(index, 'profit', costIndex)}
                                className="w-full border rounded px-2 py-1 pl-7 text-sm"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Scope group totals */}
                  <div className="mt-4 p-3 border border-blue-200 rounded-lg bg-blue-50">
                    <h5 className="text-sm font-medium text-gray-700 mb-2">Scope Group Totals</h5>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Total Builder Cost</label>
                        <div className="w-full border rounded px-2 py-1 bg-white font-medium">
                          ${calculateScopeTotals(item).builderCost.toFixed(2)}
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Total Client Price</label>
                        <div className="w-full border rounded px-2 py-1 bg-white font-medium">
                          ${calculateScopeTotals(item).clientPrice.toFixed(2)}
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Total Profit</label>
                        <div className="w-full border rounded px-2 py-1 bg-white font-medium">
                          ${calculateScopeTotals(item).profit.toFixed(2)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}

          {/* Totals Section - Bigger and Highlighted */}
          <div className="border-t pt-4 mt-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-blue-50 p-4 rounded-lg shadow-sm border-2 border-blue-200">
                <label className="block text-base font-bold text-gray-800 mb-2">
                  Total Builder Cost
                </label>
                <div className="w-full rounded px-3 py-3 bg-white text-lg font-semibold">
                  ${formData.totalBuilderCost ? Number(formData.totalBuilderCost).toFixed(2) : '0.00'}
                </div>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg shadow-sm border-2 border-blue-200">
                <label className="block text-base font-bold text-gray-800 mb-2">
                  Total Client Price
                </label>
                <div className="w-full rounded px-3 py-3 bg-white text-lg font-semibold">
                  ${formData.totalClientPrice ? Number(formData.totalClientPrice).toFixed(2) : '0.00'}
                </div>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg shadow-sm border-2 border-blue-200">
                <label className="block text-base font-bold text-gray-800 mb-2">
                  Estimated Profit
                </label>
                <div className="w-full rounded px-3 py-3 bg-white text-lg font-semibold">
                  ${formData.estimatedProfit ? Number(formData.estimatedProfit).toFixed(2) : '0.00'}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // Update the renderTemplate function to properly format address
  const renderTemplate = (displaySettings) => {
    try {
      // Make sure we have all required data
      if (!project || !formData) {
        return <div>Loading...</div>;
      }

      // Get client name from client object
      const clientName = project?.client?.name || '';

      // Format the complete address by concatenating all address fields
      const formatAddress = (address) => {
        if (!address) return '';
        const parts = [
          address.street,
          address.city && address.state ? 
            `${address.city}, ${address.state}${address.zipCode ? ' ' + address.zipCode : ''}` : 
            (address.city || address.state || address.zipCode || '')
        ].filter(Boolean);
        return parts.join(', ');
      };

      // Get address from project
      const formattedAddress = formatAddress(project?.client?.address || project?.address || {});

      // Get the most up-to-date data from currentChangeOrder if available
      let scopeItems = [];
      let costItems = [];
      
      if (currentChangeOrder && currentChangeOrder.scopeOfWork) {
        // Use the data from the current change order which should have the most up-to-date values
        scopeItems = currentChangeOrder.scopeOfWork
          .filter(scope => scope && scope.costGroup) // Filter out any null or undefined costGroups
          .map(scope => {
            // Create base item with all required fields
            const processedItem = {
              type: 'scope',
              item: scope.costGroup.name || '',
              description: scope.costGroup.description || '',
              quantity: scope.quantity || 1,
              unit: scope.unit || 'EA',
              unitCost: scope.unitCost || 0,
              builderCost: Number(scope.builderCost) || 0,
              markup: Number(scope.markup) || 49.25,
              clientPrice: Number(scope.clientPrice) || 0,
              margin: Number(scope.margin) || 33
            };
            
            // Include costCodes if this scope has associated cost codes
            if (scope.costGroup.costItems && scope.costGroup.costItems.length > 0) {
              processedItem.costCodes = scope.costGroup.costItems.map(costItem => ({
                item: costItem.item || '',
                description: costItem.description || '',
                quantity: Number(costItem.quantity) || 1,
                unit: costItem.unit || 'EA',
                unitCost: Number(costItem.unitCost) || 0,
                builderCost: Number(costItem.builderCost) || 0,
                markup: Number(costItem.markup) || 49.25,
                clientPrice: Number(costItem.clientPrice) || 0,
                margin: Number(costItem.margin) || 33
              }));
            }
            
            return processedItem;
          });
          
        // Process cost items - only include these if showCostCodes is true
        if (displaySettings.showCostCodes && currentChangeOrder.costCodes) {
          costItems = currentChangeOrder.costCodes
            .filter(code => code && code.costItem) // Filter out any null or undefined costItems
            .map(code => ({
              type: 'cost',
              item: code.costItem.item || '',
              description: code.costItem.description || '',
              quantity: Number(code.quantity) || 1,
              unit: code.unit || 'EA',
              unitCost: Number(code.unitCost) || 0,
              builderCost: Number(code.builderCost) || 0,
              markup: Number(code.markup) || 49.25,
              clientPrice: Number(code.clientPrice) || 0,
              margin: Number(code.margin) || 33
            }));
        }
      } else {
        // Fallback to using formData if currentChangeOrder is not available
        scopeItems = formData.items
          .filter(item => item.type === 'scope')
          .map(item => {
            // Create base item with all required fields
            const processedItem = {
              type: item.type,
              item: item.item || '',
              description: item.description || '',
              quantity: Number(item.quantity) || 1,
              unit: item.unit || 'EA',
              unitCost: Number(item.unitCost) || 0,
              builderCost: Number(item.builderCost) || 0,
              markup: Number(item.markup) || 49.25,
              clientPrice: Number(item.clientPrice) || 0,
              margin: Number(item.margin) || 33
            };
            
            // Include costCodes if this is a scope item with associated cost codes
            if (item.costCodes && item.costCodes.length > 0) {
              processedItem.costCodes = item.costCodes.map(costCode => ({
                item: costCode.item || '',
                description: costCode.description || '',
                quantity: Number(costCode.quantity) || 1,
                unit: costCode.unit || 'EA',
                unitCost: Number(costCode.unitCost) || 0,
                builderCost: Number(costCode.builderCost) || 0,
                markup: Number(costCode.markup) || 49.25,
                clientPrice: Number(costCode.clientPrice) || 0,
                margin: Number(costCode.margin) || 33
              }));
            }
            
            return processedItem;
          });
        
        // Process cost items - only include these if showCostCodes is true
        if (displaySettings.showCostCodes) {
          costItems = formData.items
            .filter(item => item.type === 'cost')
            .map(item => ({
              type: item.type,
              item: item.item || '',
              description: item.description || '',
              quantity: Number(item.quantity) || 1,
              unit: item.unit || 'EA',
              unitCost: Number(item.unitCost) || 0,
              builderCost: Number(item.builderCost) || 0,
              markup: Number(item.markup) || 49.25,
              clientPrice: Number(item.clientPrice) || 0,
              margin: Number(item.margin) || 33
            }));
        }
      }
      
      // Combine all items
      const allItems = [...scopeItems, ...costItems];
      
      // Calculate totals for the change order data
      const totalBuilderCost = allItems.reduce((sum, item) => sum + (Number(item.builderCost) || 0), 0);
      const totalClientPrice = allItems.reduce((sum, item) => sum + (Number(item.clientPrice) || 0), 0);
      const estimatedProfit = totalClientPrice - totalBuilderCost;

      // Add signature data if available in currentChangeOrder
      const changeOrderData = {
        ...formData,
        title: `Change Order for ${clientName}`,
        signature: currentChangeOrder?.signature || null,
        totalBuilderCost,
        totalClientPrice,
        estimatedProfit,
        builderCost: totalBuilderCost,
        clientPrice: totalClientPrice
      };

      // Generate the template with all required parameters
      const htmlContent = generateChangeOrderTemplate(
        {
          ...project,
          clientName,
          streetAddress: formattedAddress
        },
        changeOrderData,
        allItems,
        displaySettings
      );

      // Return the HTML content using dangerouslySetInnerHTML with an ID for PDF generation
      return <div id="change-order-preview-content" dangerouslySetInnerHTML={{ __html: htmlContent }} />;
    } catch (error) {
      console.error('Error in renderTemplate:', error);
      return <div>Error rendering template</div>;
    }
  };

  // Add handleDownloadProposal function
  const handleDownloadProposal = () => {
    try {
      // Get the proposal content
      const proposalElement = document.getElementById('change-order-preview-content');
      
      if (!proposalElement) {
        showToast('Unable to find change order content', 'error');
        return;
      }
      
      // Find all images in the proposal and ensure they're loaded
      const images = proposalElement.querySelectorAll('img');
      let logoImg = null;
      
      // Check if logo image exists and needs to be loaded
      for (const img of images) {
        if (img.src.includes('JRT-logo') || img.alt === 'JRT Logo') {
          logoImg = img;
          break;
        }
      }
      
      // Configure PDF options
      const options = {
        margin: 10,
        filename: `change-order-${project?.jobNumber || 'draft'}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { 
          scale: 2,
          useCORS: true,
          logging: false
        },
        jsPDF: { 
          unit: 'mm', 
          format: 'a4', 
          orientation: 'portrait' 
        }
      };

      // Create a temporary loading indicator
      const loadingIndicator = document.createElement('div');
      loadingIndicator.textContent = 'Preparing PDF...';
      loadingIndicator.style.position = 'fixed';
      loadingIndicator.style.top = '50%';
      loadingIndicator.style.left = '50%';
      loadingIndicator.style.transform = 'translate(-50%, -50%)';
      loadingIndicator.style.padding = '20px';
      loadingIndicator.style.background = 'rgba(0,0,0,0.7)';
      loadingIndicator.style.color = 'white';
      loadingIndicator.style.borderRadius = '5px';
      loadingIndicator.style.zIndex = '9999';
      document.body.appendChild(loadingIndicator);

      // Convert image to base64 for embedding in PDF
      const convertLogoToBase64 = () => {
        return new Promise((resolve) => {
          if (!logoImg) {
            resolve(); // No logo to convert
            return;
          }
          
          try {
            // Create a canvas to draw the image
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            
            // Set canvas dimensions to match the image
            canvas.width = logoImg.naturalWidth || logoImg.width;
            canvas.height = logoImg.naturalHeight || logoImg.height;
            
            // Make sure image is loaded
            if (logoImg.complete) {
              // Draw image on canvas
              ctx.drawImage(logoImg, 0, 0);
              // Convert to base64
              const dataUrl = canvas.toDataURL('image/png');
              // Replace image src with base64 data
              logoImg.src = dataUrl;
              resolve();
            } else {
              // Wait for image to load
              logoImg.onload = () => {
                // Set canvas dimensions after image is loaded
                canvas.width = logoImg.naturalWidth || logoImg.width;
                canvas.height = logoImg.naturalHeight || logoImg.height;
                // Draw image on canvas
                ctx.drawImage(logoImg, 0, 0);
                // Convert to base64
                const dataUrl = canvas.toDataURL('image/png');
                // Replace image src with base64 data
                logoImg.src = dataUrl;
                resolve();
              };
              logoImg.onerror = () => {
                console.warn('Logo image failed to load for base64 conversion');
                resolve();
              };
            }
          } catch (err) {
            console.error('Error converting logo to base64:', err);
            resolve(); // Continue despite error
          }
        });
      };

      // Wait for logo to be converted, then generate PDF
      convertLogoToBase64()
        .then(() => {
          return html2pdf()
            .set(options)
            .from(proposalElement)
            .save();
        })
        .then(() => {
          document.body.removeChild(loadingIndicator);
          showToast('Change order downloaded successfully', 'success');
        })
        .catch(error => {
          document.body.removeChild(loadingIndicator);
          console.error('Error generating PDF:', error);
          showToast('Error downloading change order', 'error');
        });
    } catch (error) {
      console.error('Error in handleDownloadProposal:', error);
      showToast('Error downloading change order', 'error');
    }
  };

  // Update handlePrint function
  const handlePrint = () => {
    const element = document.getElementById('change-order-preview-content');
    if (element) {
      printElement(element);
    }
  };

  // Style classes
  const labelClasses = "block text-xs uppercase text-gray-500 mb-1";
  const inputClasses = `
    w-full border border-gray-200 px-4 py-2.5 
    focus:outline-none focus:border-blue-500 text-gray-900
    bg-white
  `;

  // Reset draftChangeOrderId when dialog closes
  useEffect(() => {
    if (!isOpen) {
      setDraftChangeOrderId(null);
    }
  }, [isOpen]);

  // Add this handler function
  const handleDisplaySettingChange = async (setting) => {
    try {
      const newSettings = {
        ...formData.displaySettings,
        [setting]: !formData.displaySettings[setting]
      };
      
      console.log(`Changing display setting "${setting}" to ${newSettings[setting]}`);
      
      setFormData(prev => ({
        ...prev,
        displaySettings: newSettings
      }));

      if (currentChangeOrder?._id) {
        console.log('Updating change order with new display settings:', newSettings);
        await dispatch(updateChangeOrder({
          projectId: project._id,
          changeOrderId: currentChangeOrder._id,
          updates: { displaySettings: newSettings }
        })).unwrap();
        console.log('Successfully updated display settings in backend');
      }
    } catch (error) {
      console.error('Failed to update display settings:', error);
      showToast('Failed to update display settings', 'error');
    }
  };

  // Helper function to add an item to the expandedItems set
  const addToExpandedItems = (prevExpanded, itemId) => {
    if (!itemId) return prevExpanded;
    console.log('Expanding item with ID:', itemId);
    return {...prevExpanded, [itemId]: true};
  };

  // Handle opening the cost code dialog
  const handleOpenCostCodeDialog = (scopeIndex = null) => {
    console.log('📋 Opening cost code dialog with scope index:', scopeIndex);
    if (scopeIndex !== null) {
      const scopeItem = formData.items[scopeIndex];
      console.log('Associated scope item:', scopeItem);
    }
    setCurrentScopeIndex(scopeIndex);
    setShowAddCostCode(true);
  };

  // Add missing function for removing cost codes
  const handleRemoveCostCode = async (itemIndex, costIndex, costCodeId) => {
    try {
      // If the cost code has an ID and we're in an existing change order, delete it using the correct API
      if (costCodeId && currentChangeOrder?._id) {
        const scopeItem = formData.items[itemIndex];
        
        // Call the proper API for removing a cost code from a scope item
        await dispatch(removeChangeOrderCostCode({
          changeOrderId: currentChangeOrder._id,
          scopeId: scopeItem._id, // The ID of the scope item
          costCodeId: costCodeId // The ID of the cost code
        })).unwrap();
        
        // After successful API call, update the change order data
        dispatch(fetchChangeOrder({
          projectId: project._id,
          changeOrderId: currentChangeOrder._id
        }));
      }
      
      // Update local state - remove the cost code from the item
      setFormData(prev => {
        const updatedItems = [...prev.items];
        const item = {...updatedItems[itemIndex]};
        
        // Remove the cost code
        item.costCodes = item.costCodes.filter((_, index) => index !== costIndex);
        
        // Recalculate totals for the scope item
        const totals = calculateScopeTotals(item);
        item.builderCost = totals.builderCost;
        item.clientPrice = totals.clientPrice;
        item.margin = calculateMargin(totals.builderCost, totals.clientPrice);
        
        updatedItems[itemIndex] = item;
        
        // Calculate new totals for the entire change order
        const orderTotals = calculateTotals(updatedItems);
        
        return {
          ...prev,
          items: updatedItems,
          totalBuilderCost: orderTotals.totalBuilderCost,
          totalClientPrice: orderTotals.totalClientPrice,
          estimatedProfit: orderTotals.totalClientPrice - orderTotals.totalBuilderCost
        };
      });
    } catch (error) {
      console.error('Failed to remove cost code:', error);
      showToast('Failed to remove cost code: ' + (error.message || 'Unknown error'), 'error');
    }
  };

  // State for custom confirmation dialog
  const [showApprovalConfirmation, setShowApprovalConfirmation] = useState(false);
  
  // Handle showing the confirmation dialog
  const handleShowApprovalDialog = () => {
    if (!currentChangeOrder?._id || !project?._id) {
      showToast('Change order or project information is missing', 'error');
      return;
    }
    
    setShowApprovalConfirmation(true);
  };
  
  // Handle confirmation from the dialog
  const handleConfirmManualApprove = async () => {
    try {
      setShowApprovalConfirmation(false);
      
      const result = await dispatch(manualApproveChangeOrder({
        projectId: project._id,
        changeOrderId: currentChangeOrder._id
      })).unwrap();
      
      if (result && result.changeOrder) {
        showToast('Change order manually approved successfully', 'success');
      }
    } catch (error) {
      console.error('Failed to manually approve change order:', error);
      showToast(error.message || 'Failed to manually approve change order', 'error');
    }
  };

  return (
    <>
      {/* Manual Approval Confirmation Dialog */}
      {showApprovalConfirmation && (
        <div className="fixed inset-0 z-[60] overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100 sm:mx-0 sm:h-10 sm:w-10">
                    <svg className="h-6 w-6 text-yellow-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                      Manual Approval Confirmation
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Are you sure you want to manually approve this change order? This action cannot be undone.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button 
                  type="button" 
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={handleConfirmManualApprove}
                >
                  Approve
                </button>
                <button 
                  type="button" 
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={() => setShowApprovalConfirmation(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      <div 
        className={`fixed inset-0 z-50 bg-white transform transition-transform duration-300 ${
          isOpen ? 'translate-y-0' : 'translate-y-full'
        }`}
      >
        <div className="h-full flex flex-col">
          {/* Header with Tabs */}
          <div className="border-b border-gray-200">
            <div className="flex justify-between items-center px-6 py-4">
              <h2 className="text-xl font-semibold">
                {currentChangeOrder ? 'Edit Change Order' : 'Create Change Order'}
              </h2>
              <button 
                onClick={onClose}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Category Selection */}
            <div className="px-6 py-4">
              <label className={labelClasses}>Category</label>
              <select
                className={inputClasses}
                value={formData.category}
                onChange={(e) => handleCategoryChange(e.target.value)}
              >
                <option value="">Select a category</option>
                {categories?.map((category) => (
                  <option key={category._id} value={category._id}>
                    {category.name}
                  </option>
                ))}
              </select>
              {!formData.category && (
                <p className="mt-1 text-xs text-red-500">
                  Please select a category for this change order
                </p>
              )}
            </div>

            {/* Tabs */}
            <div className="px-6">
              <nav className="flex -mb-px">
                {['worksheet', 'proposal preview'].map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`mr-8 py-4 px-1 border-b-2 font-medium text-sm
                      ${activeTab === tab
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                  >
                    {tab.charAt(0).toUpperCase() + tab.slice(1)}
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 overflow-hidden">
            {activeTab === 'worksheet' ? (
              // Worksheet tab content
              <div className="h-full overflow-auto p-6">
                {renderWorksheetTab()}
              </div>
            ) : (
              // Proposal preview tab content with split view
              <div className="h-full flex">
                {/* Left side - Controls */}
                <div className="w-64 border-r border-gray-200 p-4 overflow-y-auto">
                  <ChangeOrderDisplaySettings
                    displaySettings={formData.displaySettings}
                    handleDisplaySettingChange={handleDisplaySettingChange}
                  />
                </div>

                {/* Right side - Preview */}
                <div className="flex-1 overflow-auto">
                  <div className="max-w-4xl mx-auto overflow-auto p-6">
                    <div className="flex justify-end mb-4">
                      <button
                        onClick={handleDownloadProposal}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 flex items-center gap-2"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                        Download PDF
                      </button>
                    </div>
                    {renderTemplate(formData.displaySettings)}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t bg-white">
            <div className="flex items-center justify-between">
              <button
                onClick={handlePrint}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Print
              </button>
              <div className="flex gap-2">
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveAsDraft}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Save as Draft
                </button>
                <button
                  onClick={handleShowApprovalDialog}
                  disabled={isManualApproveDisabled(currentChangeOrder?.status)}
                  className={`px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md ${
                    isManualApproveDisabled(currentChangeOrder?.status) ? 'opacity-50 cursor-not-allowed' : 'hover:bg-green-700'
                  }`}
                >
                  Manual Approve
                </button>
                <button
                  onClick={handleSaveAndRelease}
                  disabled={isReleaseDisabled(currentChangeOrder?.status)}
                  className={`px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md ${
                    isReleaseDisabled(currentChangeOrder?.status) ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-700'
                  }`}
                >
                  Save & Release
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Keep existing dialogs */}
      <AddChangeOrderScopeDialog
        isOpen={showAddScope}
        onClose={() => setShowAddScope(false)}
        onSave={handleSaveCostGroup}
        projectId={project?._id}
        changeOrderId={currentChangeOrder?._id || draftChangeOrderId}
      />

      <AddChangeOrderCostCodeDialog
        isOpen={showAddCostCode}
        onClose={() => {
          setShowAddCostCode(false);
          setCurrentScopeIndex(null); // Reset the scope index when closing the dialog
        }}
        onSave={handleSaveCostItem}
        projectId={project?._id}
        changeOrderId={currentChangeOrder?._id || draftChangeOrderId}
        scopeId={currentScopeIndex !== null ? formData.items[currentScopeIndex]?._id : null}
      />

      {/* Add a ChangeOrderCostItemDialog for direct cost item editing when selecting a cost code */}
      <ChangeOrderCostItemDialog
        isOpen={false} // We'll handle this when needed
        onClose={() => {}}
        onSave={() => {}}
        projectId={project?._id}
        changeOrderId={currentChangeOrder?._id || draftChangeOrderId}
        scopeId={currentScopeIndex !== null ? formData.items[currentScopeIndex]?._id : null}
      />
    </>
  );
};

export default ChangeOrderDialog; 